export const DASHBOARD_PERSON_TYPES = {
  ALL: "all",
  SALES: "sales",
  RECRUITER: "recruiter",
};

const _monthFormatterCache = new Map();
const getMonthFormatter = (locale) => {
  if (!_monthFormatterCache.has(locale)) {
    _monthFormatterCache.set(locale, new Intl.DateTimeFormat(locale, { month: "short" }));
  }
  return _monthFormatterCache.get(locale);
};

const POSITION_STATUS_ALIASES = {
  positioned: "positioned",
  positioning: "positioned",
  positioned_candidate: "positioned",
  positionned: "positioned",
  proposal_sent: "positioned",
  proposed: "positioned",
  awaiting_client_feedback: "awaiting_client_feedback",
  awaiting_feedback: "awaiting_client_feedback",
  waiting_client_feedback: "awaiting_client_feedback",
  client_feedback_pending: "awaiting_client_feedback",
  feedback_pending: "awaiting_client_feedback",
  signed: "signed",
  signature: "signed",
  placement_signed: "signed",
  confirmed: "signed",
  active: "signed",
  completed: "signed",
};

const DETAIL_ROW_PRIORITY_KEYS = [
  "pilot_detail_rows",
  "pilot_details",
  "positioning_rows",
  "positioning_details",
  "detail_rows",
  "details",
];

const hasOwnValue = (value) => value !== undefined && value !== null && value !== "";

const getNestedValue = (input, path) => {
  if (!input || !path) return undefined;
  return path.split(".").reduce((value, segment) => {
    if (value === undefined || value === null) return undefined;
    return value[segment];
  }, input);
};

export const readFirstValue = (input, paths = []) => {
  for (const path of paths) {
    const value = getNestedValue(input, path);
    if (hasOwnValue(value)) return value;
  }
  return undefined;
};

export const normalizeId = (value) => {
  if (!hasOwnValue(value)) return null;
  return String(value);
};

export const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const toDateInputValue = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const buildMonthKey = (year, month) => `${year}-${String(month).padStart(2, "0")}`;

export const buildMonthRange = (rangeStart, rangeEnd, locale = "en-US") => {
  const start = parseDate(rangeStart);
  const end = parseDate(rangeEnd);
  if (!start || !end) return null;

  const from = new Date(start.getFullYear(), start.getMonth(), 1);
  const to = new Date(end.getFullYear(), end.getMonth(), 1);
  if (from > to) return null;

  const formatter = getMonthFormatter(locale);
  const buckets = [];
  const cursor = new Date(from);
  while (cursor <= to) {
    buckets.push({
      year: cursor.getFullYear(),
      month: cursor.getMonth() + 1,
      label: formatter.format(cursor),
      key: buildMonthKey(cursor.getFullYear(), cursor.getMonth() + 1),
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return buckets;
};

export const buildFallbackMonths = (count = 6, locale = "en-US") => {
  const formatter = getMonthFormatter(locale);
  const buckets = [];
  const now = new Date();
  for (let index = count - 1; index >= 0; index -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    buckets.push({
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      label: formatter.format(date),
      key: buildMonthKey(date.getFullYear(), date.getMonth() + 1),
    });
  }
  return buckets;
};

export const buildWeekRangeForMonth = (bucket) => {
  if (!bucket?.year || !bucket?.month) {
    return { fromDate: "", toDate: "" };
  }
  const from = new Date(bucket.year, bucket.month - 1, 1);
  const to = new Date(bucket.year, bucket.month, 0);
  return {
    fromDate: toDateInputValue(from),
    toDate: toDateInputValue(to),
  };
};

export const humanizeLabel = (value, fallback = "-") => {
  const input = String(value || "").trim();
  if (!input) return fallback;
  const spaced = input.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const getUserId = (input) =>
  normalizeId(
    readFirstValue(input, ["id", "userId", "user_id", "user.id", "sales.id", "recruiter.id"])
  );

export const getAvatarUrl = (input) =>
  readFirstValue(input, [
    "avatar_url",
    "avatarUrl",
    "avatar",
    "user.avatar_url",
    "user.avatarUrl",
    "user.avatar",
  ]) || null;

export const getUserAvatarUrl = (input) =>
  readFirstValue(input, [
    "user.avatar_url",
    "user.avatarUrl",
    "user.avatar",
    "avatar_url",
    "avatarUrl",
    "avatar",
  ]) || null;

export const getUserDisplayName = (input, fallback = "-") => {
  const firstName = readFirstValue(input, [
    "first_name",
    "firstName",
    "user.first_name",
    "user.firstName",
  ]);
  const lastName = readFirstValue(input, [
    "last_name",
    "lastName",
    "user.last_name",
    "user.lastName",
  ]);
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  if (fullName) return fullName;

  const fallbackValue = readFirstValue(input, [
    "name",
    "full_name",
    "fullName",
    "user.name",
    "user.full_name",
    "user.fullName",
    "email",
    "user.email",
  ]);

  return fallbackValue || fallback;
};

export const getInitials = (name) => {
  const parts = String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (!parts.length) return "?";
  return parts.map((part) => part[0].toUpperCase()).join("");
};

export const getFullNameFromPaths = (input, config) => {
  const firstName = readFirstValue(input, config.first || []);
  const lastName = readFirstValue(input, config.last || []);
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  if (fullName) return fullName;
  const fallback = readFirstValue(input, config.fallback || []);
  return fallback || "-";
};

const isDetailRowCandidate = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  return Boolean(
    readFirstValue(value, [
      "consultant_name",
      "consultantName",
      "consultant.first_name",
      "candidate_first_name",
      "client_name",
      "clientName",
      "job_title",
      "jobTitle",
      "positioning_status",
      "positioningStatus",
      "status",
    ])
  );
};

const findDetailRowArray = (businessUnit) => {
  for (const key of DETAIL_ROW_PRIORITY_KEYS) {
    if (Array.isArray(businessUnit?.[key])) return businessUnit[key];
  }

  const values = Object.values(businessUnit || {});
  return (
    values.find(
      (value) => Array.isArray(value) && value.some((item) => isDetailRowCandidate(item))
    ) || []
  );
};

const normalizeStatusValue = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return POSITION_STATUS_ALIASES[normalized] || normalized || "unknown";
};

export const getPositionStatusLabelKey = (value) => {
  const normalized = normalizeStatusValue(value);
  if (normalized === "awaiting_client_feedback") return "awaitingClientFeedback";
  if (normalized === "signed") return "signed";
  if (normalized === "positioned") return "positioned";
  return "fallback";
};

export const normalizeDetailRow = (row, index = 0) => {
  const consultantName = getFullNameFromPaths(row, {
    first: [
      "consultant.first_name",
      "consultantFirstName",
      "consultant_first_name",
      "candidate_first_name",
    ],
    last: [
      "consultant.last_name",
      "consultantLastName",
      "consultant_last_name",
      "candidate_last_name",
    ],
    fallback: [
      "consultant_name",
      "consultantName",
      "candidate_name",
      "candidateName",
      "consultant.email",
      "consultant_email",
    ],
  });

  const salesId = normalizeId(
    readFirstValue(row, ["sales.id", "salesId", "sales_id", "sales_user_id", "salesUserId"])
  );
  const recruiterId = normalizeId(
    readFirstValue(row, [
      "recruiter.id",
      "recruiterId",
      "recruiter_id",
      "recruiter_user_id",
      "recruiterUserId",
    ])
  );

  return {
    id:
      normalizeId(
        readFirstValue(row, [
          "id",
          "match_id",
          "matchId",
          "positioning_id",
          "positioningId",
          "need_id",
          "needId",
        ])
      ) || `detail-row-${index}`,
    consultantName,
    clientName:
      readFirstValue(row, [
        "client.name",
        "clientName",
        "client_name",
        "company.name",
        "companyName",
        "company_name",
      ]) || "-",
    jobTitle:
      readFirstValue(row, ["job_title", "jobTitle", "need_title", "needTitle", "title"]) || "-",
    rawStatus:
      readFirstValue(row, ["positioning_status", "positioningStatus", "match_status", "status"]) ||
      "unknown",
    normalizedStatus: normalizeStatusValue(
      readFirstValue(row, ["positioning_status", "positioningStatus", "match_status", "status"])
    ),
    salesId,
    recruiterId,
    salesName: getFullNameFromPaths(row, {
      first: ["sales.first_name", "sales_first_name", "salesFirstName"],
      last: ["sales.last_name", "sales_last_name", "salesLastName"],
      fallback: ["sales_name", "salesName", "sales.email", "sales_email"],
    }),
    recruiterName: getFullNameFromPaths(row, {
      first: ["recruiter.first_name", "recruiter_first_name", "recruiterFirstName"],
      last: ["recruiter.last_name", "recruiter_last_name", "recruiterLastName"],
      fallback: ["recruiter_name", "recruiterName", "recruiter.email", "recruiter_email"],
    }),
    createdAt: readFirstValue(row, ["createdAt", "created_at", "match_created_at"]) || null,
  };
};

export const filterRowsByPerson = (rows = [], filters) => {
  if (!Array.isArray(rows)) return [];
  if (filters?.personType === DASHBOARD_PERSON_TYPES.SALES && filters?.salesId) {
    return rows.filter((row) => String(row.salesId || "") === String(filters.salesId));
  }
  if (filters?.personType === DASHBOARD_PERSON_TYPES.RECRUITER && filters?.recruiterId) {
    return rows.filter((row) => String(row.recruiterId || "") === String(filters.recruiterId));
  }
  return rows;
};

export const getSelectedBusinessUnits = (statistics, selectedBusinessUnitIds = []) => {
  const businessUnits = Array.isArray(statistics?.business_units) ? statistics.business_units : [];
  if (!selectedBusinessUnitIds.length) return businessUnits;
  const selectedSet = new Set(selectedBusinessUnitIds.map((value) => String(value)));
  return businessUnits.filter((item) =>
    selectedSet.has(String(item?.business_unit_id ?? item?.id ?? ""))
  );
};

const initSeriesMap = (monthBuckets = []) => {
  const map = new Map();
  monthBuckets.forEach((bucket) => {
    map.set(bucket.key, 0);
  });
  return map;
};

const addSeriesCounts = (map, items = []) => {
  items.forEach((item) => {
    const year = safeNumber(item?.year);
    const month = safeNumber(item?.month);
    if (!year || !month) return;
    const key = buildMonthKey(year, month);
    if (!map.has(key)) return;
    map.set(key, map.get(key) + safeNumber(item?.count));
  });
};

const mapSeriesValues = (map, monthBuckets = []) =>
  monthBuckets.map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
    year: bucket.year,
    month: bucket.month,
    value: safeNumber(map.get(bucket.key)),
  }));

const mergeRankingItems = (items = [], metricKeys = []) => {
  const merged = new Map();
  items.forEach((item) => {
    const id = getUserId(item);
    if (!id) return;

    const existing = merged.get(id) || {
      user_id: id,
      avatar_url: getUserAvatarUrl(item),
      first_name: readFirstValue(item, ["first_name", "firstName", "user.first_name", "user.firstName"]) || null,
      last_name: readFirstValue(item, ["last_name", "lastName", "user.last_name", "user.lastName"]) || null,
      name: getUserDisplayName(item),
    };

    metricKeys.forEach((key) => {
      existing[key] = safeNumber(existing[key]) + safeNumber(item?.[key]);
    });

    if (!existing.avatar_url) {
      existing.avatar_url = getUserAvatarUrl(item);
    }
    if ((!existing.first_name || !existing.last_name) && item) {
      existing.first_name = existing.first_name || readFirstValue(item, ["first_name", "firstName"]);
      existing.last_name = existing.last_name || readFirstValue(item, ["last_name", "lastName"]);
      existing.name = getUserDisplayName(item, existing.name);
    }

    merged.set(id, existing);
  });
  return Array.from(merged.values());
};

export const buildSeriesFromStatistics = (statistics, selectedBusinessUnitIds, monthBuckets) => {
  const selectedBusinessUnits = getSelectedBusinessUnits(statistics, selectedBusinessUnitIds);
  const seriesMaps = {
    cvSeries: initSeriesMap(monthBuckets),
    skillsSeries: initSeriesMap(monthBuckets),
    needsCreatedSeries: initSeriesMap(monthBuckets),
    needsCoveredSeries: initSeriesMap(monthBuckets),
    matchesProposedSeries: initSeriesMap(monthBuckets),
    matchesConfirmedSeries: initSeriesMap(monthBuckets),
  };

  selectedBusinessUnits.forEach((businessUnit) => {
    addSeriesCounts(seriesMaps.cvSeries, businessUnit?.cvs_processed);
    addSeriesCounts(seriesMaps.skillsSeries, businessUnit?.assessments_sent);
    addSeriesCounts(seriesMaps.needsCreatedSeries, businessUnit?.needs_created);
    addSeriesCounts(seriesMaps.needsCoveredSeries, businessUnit?.needs_covered);
    addSeriesCounts(seriesMaps.matchesProposedSeries, businessUnit?.matches_proposed);
    addSeriesCounts(seriesMaps.matchesConfirmedSeries, businessUnit?.matches_confirmed);
  });

  return {
    cvSeries: mapSeriesValues(seriesMaps.cvSeries, monthBuckets),
    skillsSeries: mapSeriesValues(seriesMaps.skillsSeries, monthBuckets),
    needsCreatedSeries: mapSeriesValues(seriesMaps.needsCreatedSeries, monthBuckets),
    needsCoveredSeries: mapSeriesValues(seriesMaps.needsCoveredSeries, monthBuckets),
    matchesProposedSeries: mapSeriesValues(seriesMaps.matchesProposedSeries, monthBuckets),
    matchesConfirmedSeries: mapSeriesValues(seriesMaps.matchesConfirmedSeries, monthBuckets),
  };
};

export const sumSeries = (series = []) =>
  series.reduce((total, item) => total + safeNumber(item?.value), 0);

export const buildPilotDetailRows = (statistics, selectedBusinessUnitIds, filters) => {
  const rows = getSelectedBusinessUnits(statistics, selectedBusinessUnitIds).flatMap((businessUnit) =>
    findDetailRowArray(businessUnit).map((row, index) => normalizeDetailRow(row, index))
  );

  return filterRowsByPerson(rows, filters);
};

export const buildRankings = (statistics, selectedBusinessUnitIds, filters) => {
  const selectedBusinessUnits = getSelectedBusinessUnits(statistics, selectedBusinessUnitIds);

  const recruiterRanking = mergeRankingItems(
    selectedBusinessUnits.flatMap((businessUnit) => businessUnit?.recruiter_ranking || []),
    ["proposals", "interviews", "placements"]
  )
    .sort(
      (left, right) =>
        safeNumber(right.placements) - safeNumber(left.placements) ||
        safeNumber(right.interviews) - safeNumber(left.interviews) ||
        safeNumber(right.proposals) - safeNumber(left.proposals) ||
        String(left.name || "").localeCompare(String(right.name || ""))
    )
    .map((item, index) => ({ ...item, rank: index + 1 }));

  const salesRanking = mergeRankingItems(
    selectedBusinessUnits.flatMap((businessUnit) => businessUnit?.sales_ranking || []),
    ["needs_opened", "conversions"]
  )
    .sort(
      (left, right) =>
        safeNumber(right.conversions) - safeNumber(left.conversions) ||
        safeNumber(right.needs_opened) - safeNumber(left.needs_opened) ||
        String(left.name || "").localeCompare(String(right.name || ""))
    )
    .map((item, index) => ({ ...item, rank: index + 1 }));

  return { recruiterRanking, salesRanking };
};

export const buildPersonOptions = ({
  statistics,
  selectedBusinessUnitIds,
  fallbackUser,
  fallbackRole,
}) => {
  const selectedBusinessUnits = getSelectedBusinessUnits(statistics, selectedBusinessUnitIds);
  const recruiterOptions = new Map();
  const salesOptions = new Map();

  const addOption = (map, item) => {
    const id = getUserId(item);
    if (!id) return;
    map.set(id, {
      id,
      name: getUserDisplayName(item),
    });
  };

  selectedBusinessUnits.forEach((businessUnit) => {
    (businessUnit?.recruiter_ranking || []).forEach((item) => addOption(recruiterOptions, item));
    (businessUnit?.sales_ranking || []).forEach((item) => addOption(salesOptions, item));
    findDetailRowArray(businessUnit).forEach((row) => {
      const normalized = normalizeDetailRow(row);
      if (normalized.recruiterId) {
        recruiterOptions.set(normalized.recruiterId, {
          id: normalized.recruiterId,
          name: normalized.recruiterName,
        });
      }
      if (normalized.salesId) {
        salesOptions.set(normalized.salesId, {
          id: normalized.salesId,
          name: normalized.salesName,
        });
      }
    });
  });

  if (fallbackUser?.id && fallbackRole === DASHBOARD_PERSON_TYPES.RECRUITER) {
    recruiterOptions.set(String(fallbackUser.id), {
      id: String(fallbackUser.id),
      name: getUserDisplayName(fallbackUser),
    });
  }

  if (fallbackUser?.id && fallbackRole === DASHBOARD_PERSON_TYPES.SALES) {
    salesOptions.set(String(fallbackUser.id), {
      id: String(fallbackUser.id),
      name: getUserDisplayName(fallbackUser),
    });
  }

  const sortByName = (left, right) => String(left.name || "").localeCompare(String(right.name || ""));

  return {
    recruiterOptions: Array.from(recruiterOptions.values()).sort(sortByName),
    salesOptions: Array.from(salesOptions.values()).sort(sortByName),
  };
};

export const buildPilotChartData = (seriesByMetric = {}, monthBuckets = []) =>
  monthBuckets.map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
    year: bucket.year,
    month: bucket.month,
    needsCreated: safeNumber(
      seriesByMetric.needsCreatedSeries?.find((item) => item.key === bucket.key)?.value
    ),
    needsCovered: safeNumber(
      seriesByMetric.needsCoveredSeries?.find((item) => item.key === bucket.key)?.value
    ),
    matchesProposed: safeNumber(
      seriesByMetric.matchesProposedSeries?.find((item) => item.key === bucket.key)?.value
    ),
    matchesConfirmed: safeNumber(
      seriesByMetric.matchesConfirmedSeries?.find((item) => item.key === bucket.key)?.value
    ),
  }));

export const hasAnySeriesData = (series = []) =>
  Array.isArray(series) && series.some((item) => safeNumber(item?.value) > 0);

export const getWeekRange = (offset = 0) => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0=Sun
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7) + offset * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    fromDate: toDateInputValue(monday),
    toDate: toDateInputValue(sunday),
    label: `${toDateInputValue(monday)} – ${toDateInputValue(sunday)}`,
  };
};

const _getISOWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return {
    year: d.getUTCFullYear(),
    week: Math.ceil((((d - yearStart) / 86400000) + 1) / 7),
  };
};

export const buildWeekKey = (year, week) => `${year}-W${String(week).padStart(2, "0")}`;

const _weekLabelFormatterCache = new Map();
const getWeekLabelFormatter = (locale) => {
  if (!_weekLabelFormatterCache.has(locale)) {
    _weekLabelFormatterCache.set(locale, new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }));
  }
  return _weekLabelFormatterCache.get(locale);
};

export const buildWeekBuckets = (count = 12, locale = "en-US") => {
  const formatter = getWeekLabelFormatter(locale);
  const today = new Date();
  const dayOfWeek = today.getDay();
  const currentMonday = new Date(today);
  currentMonday.setDate(today.getDate() - ((dayOfWeek + 6) % 7));
  const buckets = [];
  for (let i = count - 1; i >= 0; i--) {
    const monday = new Date(currentMonday);
    monday.setDate(currentMonday.getDate() - i * 7);
    const { year, week } = _getISOWeek(monday);
    buckets.push({
      year,
      week,
      label: formatter.format(monday),
      key: buildWeekKey(year, week),
    });
  }
  return buckets;
};

const addWeekSeriesCounts = (map, items = []) => {
  items.forEach((item) => {
    const year = safeNumber(item?.year);
    const week = safeNumber(item?.month); // backend stores ISO week number in month field
    if (!year || !week) return;
    const key = buildWeekKey(year, week);
    if (!map.has(key)) return;
    map.set(key, map.get(key) + safeNumber(item?.count));
  });
};

export const buildWeekSeriesFromStatistics = (statistics, selectedBusinessUnitIds, weekBuckets) => {
  const selectedBusinessUnits = getSelectedBusinessUnits(statistics, selectedBusinessUnitIds);
  const seriesMaps = {
    cvSeries: initSeriesMap(weekBuckets),
    skillsSeries: initSeriesMap(weekBuckets),
    needsCreatedSeries: initSeriesMap(weekBuckets),
    needsCoveredSeries: initSeriesMap(weekBuckets),
    matchesProposedSeries: initSeriesMap(weekBuckets),
    matchesConfirmedSeries: initSeriesMap(weekBuckets),
  };
  selectedBusinessUnits.forEach((businessUnit) => {
    addWeekSeriesCounts(seriesMaps.cvSeries, businessUnit?.cvs_processed);
    addWeekSeriesCounts(seriesMaps.skillsSeries, businessUnit?.assessments_sent);
    addWeekSeriesCounts(seriesMaps.needsCreatedSeries, businessUnit?.needs_created);
    addWeekSeriesCounts(seriesMaps.needsCoveredSeries, businessUnit?.needs_covered);
    addWeekSeriesCounts(seriesMaps.matchesProposedSeries, businessUnit?.matches_proposed);
    addWeekSeriesCounts(seriesMaps.matchesConfirmedSeries, businessUnit?.matches_confirmed);
  });
  return {
    cvSeries: mapSeriesValues(seriesMaps.cvSeries, weekBuckets),
    skillsSeries: mapSeriesValues(seriesMaps.skillsSeries, weekBuckets),
    needsCreatedSeries: mapSeriesValues(seriesMaps.needsCreatedSeries, weekBuckets),
    needsCoveredSeries: mapSeriesValues(seriesMaps.needsCoveredSeries, weekBuckets),
    matchesProposedSeries: mapSeriesValues(seriesMaps.matchesProposedSeries, weekBuckets),
    matchesConfirmedSeries: mapSeriesValues(seriesMaps.matchesConfirmedSeries, weekBuckets),
  };
};

export const isRowNew = (row, weekRange) => {
  if (!weekRange || !row?.createdAt) return false;
  const created = parseDate(row.createdAt);
  if (!created) return false;
  const from = parseDate(weekRange.fromDate);
  const to = parseDate(weekRange.toDate);
  if (!from || !to) return false;
  // Compare date-only (ignore time) so rows created anywhere in the day count
  const createdDay = new Date(created.getFullYear(), created.getMonth(), created.getDate());
  const fromDay = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const toDay = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  return createdDay >= fromDay && createdDay <= toDay;
};
